import {
  ResetButton,
  SearchButton,
} from 'Pages/reports/[submodules]/employees/components/EmployeesReportFilters/EmployeesReportFilters.styled';
import { EmployeesByCourseReportFilterI } from 'types';
import useCourseEmployeesReportFilters from '../../hooks/useCourseEmployeesReportFilters';
import { Controller } from 'react-hook-form';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import SearchTextField from 'Components/SeachTextField/SearchTextField';
import CustomSelect from 'Components/CustomSelect/CustomSelect';
import CustomMultipleSelect from 'Components/CustomMultipleSelect/CustomMultipleSelect';
import { ageRangeOptions } from 'Pages/reports/[submodules]/employees/schemas/employees-report-schemas';
import { CourseEmployeesFiltersContainer } from './CourseEmployeesFilters.styled';
import { colors } from 'Constants/Colors';
import { Search } from '@mui/icons-material';

interface Props {
  onSearch: (filters: EmployeesByCourseReportFilterI) => void;
}

const CourseEmployeesFilters = ({ onSearch }: Props) => {
  const {
    control,
    onReset,
    t2,
    terminalsData,
    departmentsData,
    jobTitlesData,
    jobTitlesTypesData,
    setValue,
    getValues,
    isDirty,
  } = useCourseEmployeesReportFilters({ onSearch });

  return (
    <CourseEmployeesFiltersContainer>
      <div style={{ display: 'flex', gap: 20, alignItems: 'end' }}>
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => <SearchTextField placeholder={t2('name_or_payroll')} {...field} />}
        />

        <Controller
          name="entryDate"
          control={control}
          render={({ field }) => <CustomDatePicker label={t2('entry_date')} {...field} />}
        />

        <SearchButton
          variant="outlined"
          color="success"
          hidden={!isDirty}
          startIcon={<Search />}
          onClick={() => {
            const values = getValues();
            onSearch(values);
          }}
        >
          {t2('search')}
        </SearchButton>

        {isDirty && (
          <ResetButton
            onClick={onReset}
            variant="outlined"
            style={{
              color: colors.RED,
              borderColor: colors.RED,
              textTransform: 'none',
              height: '50px',
              minWidth: '100px',
            }}
          >
            {t2('clear_filters')}
          </ResetButton>
        )}
      </div>

      <div style={{ display: 'flex', gap: 30, alignItems: 'end' }}>
        <Controller
          name="terminalId"
          control={control}
          render={({ field }) => (
            <CustomMultipleSelect
              placeholder={t2('terminal')}
              options={terminalsData?.map(terminal => ({
                value: terminal.id,
                label: terminal.name,
              }))}
              {...field}
            />
          )}
        />

        <Controller
          name="departmentId"
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder={t2('department')}
              options={departmentsData?.map(department => ({
                value: department.id,
                label: department.name,
              }))}
              {...field}
            />
          )}
        />

        <Controller
          name="jobTitleId"
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder={t2('job_title')}
              options={jobTitlesData?.map(jobTitle => ({
                value: jobTitle.id,
                label: jobTitle.name,
              }))}
              {...field}
            />
          )}
        />

        <Controller
          name="jobTitleTypeId"
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder={t2('job_title_type')}
              options={jobTitlesTypesData?.map(type => ({
                value: type.id,
                label: type.name,
              }))}
              {...field}
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder={t2('gender')}
              options={[
                { value: 0, label: t2('male') },
                { value: 1, label: t2('female') },
              ]}
              {...field}
            />
          )}
        />

        <Controller
          name="ageRange"
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder={t2('age_range')}
              options={[
                { value: 0, label: t2('young') },
                { value: 1, label: t2('adult') },
                { value: 2, label: t2('elder') },
              ]}
              {...field}
              onChange={event => {
                field.onChange(event);
                const value = event?.target?.value;

                if (value === null) {
                  setValue('minAge', null);
                  setValue('maxAge', null);
                  return;
                }

                const [min, max] = ageRangeOptions[value];
                setValue('minAge', min);
                setValue('maxAge', max);
              }}
            />
          )}
        />
      </div>
    </CourseEmployeesFiltersContainer>
  );
};

export default CourseEmployeesFilters;
